import { useState } from 'react';
import {
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  Plus,
  X,
  Check,
} from 'lucide-react';
import {
  useChannels,
  useSyncChannels,
  useChannelSchedule,
  useUpdateSchedule,
  useUpdateChannel,
  useSocialSettings,
  useTestPublerConnection,
} from '@/hooks/useSocial';
import { Button, Card, EmptyState, Select, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { PubChannel, SocialNetwork } from '@/types';

const NETWORK_LABELS: Record<SocialNetwork, string> = {
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  TWITTER: 'X / Twitter',
  LINKEDIN: 'LinkedIn',
  PINTEREST: 'Pinterest',
  TIKTOK: 'TikTok',
  YOUTUBE: 'YouTube',
  GOOGLE_BUSINESS: 'Google Business',
  TELEGRAM: 'Telegram',
  WORDPRESS: 'WordPress',
  MASTODON: 'Mastodon',
  THREADS: 'Threads',
  BLUESKY: 'Bluesky',
};

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: String(i).padStart(2, '0'),
}));

const MINUTE_OPTIONS = [0, 15, 30, 45].map((m) => ({
  value: String(m),
  label: String(m).padStart(2, '0'),
}));

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'UTC', label: 'UTC' },
];

export default function SocialChannelSettings() {
  const { data: channels = [], isLoading } = useChannels();
  const syncMutation = useSyncChannels();
  const testMutation = useTestPublerConnection();
  useSocialSettings(); // Prefetch settings

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-heading-3 text-secondary">Social Channels</h2>
          <p className="text-body-sm text-grey-500 mt-1">
            Manage your Publer channels and posting schedules
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => testMutation.mutate()}
            loading={testMutation.isPending}
          >
            {testMutation.data?.success
              ? `Connected (${testMutation.data.channels} channels)`
              : 'Test Connection'}
          </Button>
          <Button
            onClick={() => syncMutation.mutate()}
            loading={syncMutation.isPending}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Sync Channels
          </Button>
        </div>
      </div>

      {/* Connection status */}
      {testMutation.data && (
        <Card padding="sm">
          <div className="flex items-center gap-3">
            {testMutation.data.success ? (
              <>
                <Check className="w-5 h-5 text-success" />
                <span className="text-sm text-secondary">
                  Connected to Publer — {testMutation.data.channels} channel
                  {testMutation.data.channels !== 1 ? 's' : ''} available
                </span>
              </>
            ) : (
              <>
                <X className="w-5 h-5 text-error" />
                <span className="text-sm text-error">
                  Connection failed. Check your PUBLER_API_KEY and
                  PUBLER_WORKSPACE_ID environment variables.
                </span>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Channels list */}
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      ) : channels.length === 0 ? (
        <Card>
          <EmptyState
            icon={<RefreshCw />}
            title="No channels synced yet"
            description='Click "Sync Channels" to import from Publer'
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {channels.map((channel) => (
            <ChannelCard key={channel.id} channel={channel} />
          ))}
        </div>
      )}
    </div>
  );
}

function ChannelCard({ channel }: { channel: PubChannel }) {
  const [expanded, setExpanded] = useState(false);
  const updateChannel = useUpdateChannel();

  return (
    <Card padding="none" className="overflow-hidden">
      <div
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-grey-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {channel.avatar && (
            <img
              src={channel.avatar}
              alt=""
              className="w-8 h-8 rounded-full object-cover"
            />
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-secondary">{channel.name}</span>
              <Badge variant={channel.isActive ? 'success' : 'default'}>
                {channel.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <span className="text-xs text-grey-400">
              {NETWORK_LABELS[channel.network]}
              {channel.schedule?.isActive && (
                <> &middot; {(channel.schedule.timeSlots as any[])?.length || 0} time slots</>
              )}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label
            className="flex items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={channel.isActive}
              onChange={() =>
                updateChannel.mutate({
                  id: channel.id,
                  data: { isActive: !channel.isActive },
                })
              }
              className="rounded border-grey-300 text-primary focus:ring-primary mr-2"
            />
            <span className="text-xs text-grey-400">Active</span>
          </label>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-grey-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-grey-400" />
          )}
        </div>
      </div>

      {expanded && <ScheduleEditor channelId={channel.id} />}
    </Card>
  );
}

function ScheduleEditor({ channelId }: { channelId: string }) {
  const { data: schedule, isLoading } = useChannelSchedule(channelId);
  const updateMutation = useUpdateSchedule();

  const [newHour, setNewHour] = useState('9');
  const [newMinute, setNewMinute] = useState('0');

  if (isLoading || !schedule) {
    return (
      <div className="px-4 pb-4">
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    );
  }

  const timeSlots = (schedule.timeSlots || []) as Array<{
    hour: number;
    minute: number;
  }>;

  const addSlot = () => {
    const hour = parseInt(newHour);
    const minute = parseInt(newMinute);
    if (isNaN(hour) || isNaN(minute)) return;
    if (timeSlots.some((s) => s.hour === hour && s.minute === minute)) return;

    const newSlots = [...timeSlots, { hour, minute }].sort(
      (a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute),
    );

    updateMutation.mutate({
      channelId,
      data: { timeSlots: newSlots },
    });
  };

  const removeSlot = (index: number) => {
    const newSlots = timeSlots.filter((_, i) => i !== index);
    updateMutation.mutate({
      channelId,
      data: { timeSlots: newSlots },
    });
  };

  const toggleDay = (day: number) => {
    const newDays = schedule.daysOfWeek.includes(day)
      ? schedule.daysOfWeek.filter((d) => d !== day)
      : [...schedule.daysOfWeek, day].sort();

    updateMutation.mutate({
      channelId,
      data: { daysOfWeek: newDays },
    });
  };

  const toggleActive = () => {
    updateMutation.mutate({
      channelId,
      data: { isActive: !schedule.isActive },
    });
  };

  return (
    <div className="border-t border-grey-200 px-4 pb-4 pt-3 space-y-4">
      {/* Schedule active toggle */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-secondary">
          <Clock className="w-4 h-4 inline mr-1" />
          Posting Schedule
        </span>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-grey-400">
            {schedule.isActive ? 'Enabled' : 'Disabled'}
          </span>
          <input
            type="checkbox"
            checked={schedule.isActive}
            onChange={toggleActive}
            className="rounded border-grey-300 text-primary focus:ring-primary"
          />
        </label>
      </div>

      {/* Days of week */}
      <div>
        <label className="text-xs text-grey-500 font-medium mb-1.5 block">
          Days
        </label>
        <div className="flex gap-1">
          {DAYS_OF_WEEK.map((day) => (
            <button
              key={day.value}
              onClick={() => toggleDay(day.value)}
              className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
                schedule.daysOfWeek.includes(day.value)
                  ? 'bg-primary text-white'
                  : 'bg-grey-100 text-grey-500 hover:bg-grey-200'
              }`}
            >
              {day.label}
            </button>
          ))}
        </div>
      </div>

      {/* Time slots */}
      <div>
        <label className="text-xs text-grey-500 font-medium mb-1.5 block">
          Time Slots
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {timeSlots.map((slot, i) => (
            <div
              key={i}
              className="flex items-center gap-1 bg-grey-100 rounded-lg px-3 py-1.5"
            >
              <span className="text-sm text-secondary">
                {String(slot.hour).padStart(2, '0')}:
                {String(slot.minute).padStart(2, '0')}
              </span>
              <button
                onClick={() => removeSlot(i)}
                className="text-grey-400 hover:text-error ml-1"
                aria-label={`Remove ${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {timeSlots.length === 0 && (
            <span className="text-xs text-grey-400">No time slots set</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Select
            aria-label="Hour"
            selectSize="sm"
            value={newHour}
            onChange={(e) => setNewHour(e.target.value)}
            options={HOUR_OPTIONS}
          />
          <span className="text-grey-400">:</span>
          <Select
            aria-label="Minute"
            selectSize="sm"
            value={newMinute}
            onChange={(e) => setNewMinute(e.target.value)}
            options={MINUTE_OPTIONS}
          />
          <Button variant="ghost" size="sm" onClick={addSlot}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Timezone */}
      <div>
        <Select
          label="Timezone"
          value={schedule.timezone}
          onChange={(e) =>
            updateMutation.mutate({
              channelId,
              data: { timezone: e.target.value },
            })
          }
          options={TIMEZONES}
        />
      </div>
    </div>
  );
}
